import { useEffect, type ReactNode } from 'react'
import './LegalPage.css'
import { syncLegalDocumentSeo, type LegalDocumentKind } from './seo'

type LegalSection = {
  id: string
  title: string
  content: ReactNode
}

const company = {
  name: 'GOLEMWORKERS LLC',
  address: '30 N Gould St Ste N, Sheridan, WY 82801, United States',
  email: 'info@sidewisp.com',
}

const termsSections: LegalSection[] = [
  {
    id: 'service',
    title: '1. The Service',
    content: (
      <>
        <p>
          Sidewisp is an AI-agent monitoring, diagnostics, and recovery-support product. It may provide health
          signals, incident explanations, suggested actions, approval workflows, usage insights, notifications,
          integrations, and related features.
        </p>
        <p>
          Some features are offered as a private preview, beta, or early-access service. Features, limits,
          integrations, and availability may change as the product develops.
        </p>
      </>
    ),
  },
  {
    id: 'eligibility',
    title: '2. Eligibility and Accounts',
    content: (
      <>
        <p>You must be at least 18 years old and legally able to enter into these Terms.</p>
        <p>
          Consumer accounts use Google or Apple identity. You are responsible for keeping the provider account and
          devices you use with Sidewisp secure, and for activity performed through your account or connected
          systems. If you use Sidewisp for an organization, you represent that you have authority to bind it.
        </p>
      </>
    ),
  },
  {
    id: 'connections',
    title: '3. Connected Systems and Permissions',
    content: (
      <>
        <p>
          You choose which agents, runtimes, accounts, and services to connect. You authorize us to access and
          process only the data and permissions necessary to provide the features you enable.
        </p>
        <p>
          You must have all rights and permissions required to connect a system and provide its data. You remain
          responsible for the configuration, security, backups, and lawful operation of your own systems.
        </p>
      </>
    ),
  },
  {
    id: 'authority',
    title: '4. Human Authority and Suggested Actions',
    content: (
      <>
        <p>
          Sidewisp may diagnose issues, recommend next steps, or prepare an action for review. A recommendation is
          not a guarantee that an issue exists or that the proposed action will solve it.
        </p>
        <p>
          Where Sidewisp asks for approval, you are responsible for reviewing the exact scope and consequences
          before approving. You remain responsible for decisions and actions taken in your environment.
        </p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: '5. Acceptable Use',
    content: (
      <>
        <p>You may not use Sidewisp to:</p>
        <ul>
          <li>violate law, regulation, contractual obligations, or another person’s rights;</li>
          <li>gain unauthorized access to systems, credentials, data, or accounts;</li>
          <li>distribute malware, facilitate fraud, harassment, or other harmful conduct;</li>
          <li>circumvent safeguards, rate limits, access controls, or usage restrictions;</li>
          <li>interfere with the Service or use it to build or benchmark a competing service unlawfully; or</li>
          <li>submit data that you are not authorized to process.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'preview',
    title: '6. Preview and Beta Features',
    content: (
      <p>
        Preview and beta features may be incomplete, inaccurate, unavailable, or changed without notice. They are
        provided for evaluation and should not be your sole method of monitoring critical systems, preserving data,
        satisfying compliance obligations, or responding to emergencies.
      </p>
    ),
  },
  {
    id: 'fees',
    title: '7. Fees and Payment',
    content: (
      <>
        <p>
          Early-access features may be free. If paid plans are offered, pricing, billing period, included usage,
          renewal terms, and applicable taxes will be disclosed before purchase.
        </p>
        <p>
          Mobile subscriptions are purchased through Apple App Store or Google Play. The originating store displays
          the price, trial, renewal, cancellation, tax, and refund terms and manages payment. Sidewisp verifies store
          transactions and notifications to decide which product access is active. You must use that store to manage
          or cancel the subscription and to request any refund available under its rules or applicable law.
        </p>
      </>
    ),
  },
  {
    id: 'ownership',
    title: '8. Ownership and Feedback',
    content: (
      <>
        <p>
          We and our licensors own Sidewisp, including its software, design, branding, documentation, and related
          intellectual property. These Terms grant you a limited, non-exclusive, non-transferable right to use the
          Service while the Terms apply.
        </p>
        <p>
          You retain ownership of your data. You grant us the limited rights needed to host, process, transmit, and
          display it to provide, secure, support, and improve the Service. If you provide feedback, we may use it
          without restriction or compensation.
        </p>
      </>
    ),
  },
  {
    id: 'third-parties',
    title: '9. Third-Party Services',
    content: (
      <p>
        Sidewisp may interoperate with third-party runtimes, model providers, messaging services, Google and Apple
        identity services, Apple App Store, Google Play, and infrastructure. Their terms and privacy practices apply
        to your use of those services. We do not control and are not responsible for third-party services.
      </p>
    ),
  },
  {
    id: 'availability',
    title: '10. Availability and Changes',
    content: (
      <p>
        We may modify, suspend, or discontinue any part of Sidewisp. We do not guarantee uninterrupted operation,
        complete detection of failures, recovery of lost work, or compatibility with every agent or integration.
        Maintenance, security events, provider outages, and circumstances outside our control may affect the
        Service.
      </p>
    ),
  },
  {
    id: 'termination',
    title: '11. Suspension and Termination',
    content: (
      <p>
        You may stop using Sidewisp at any time. We may suspend or terminate access if you violate these Terms,
        create security or legal risk, fail to pay applicable fees, or if continued operation is no longer
        practicable. Provisions that by their nature should survive termination will survive.
      </p>
    ),
  },
  {
    id: 'disclaimer',
    title: '12. Disclaimers',
    content: (
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, SIDEWISP IS PROVIDED “AS IS” AND “AS AVAILABLE.” WE DISCLAIM ALL
        EXPRESS OR IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
        NON-INFRINGEMENT, ACCURACY, AND RELIABILITY. SIDEWISP DOES NOT PROVIDE LEGAL, FINANCIAL, MEDICAL, OR OTHER
        PROFESSIONAL ADVICE.
      </p>
    ),
  },
  {
    id: 'liability',
    title: '13. Limitation of Liability',
    content: (
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, GOLEMWORKERS LLC WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL,
        SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, DATA, GOODWILL, OR
        BUSINESS INTERRUPTION. OUR TOTAL LIABILITY ARISING OUT OF OR RELATING TO SIDEWISP WILL NOT EXCEED THE AMOUNT
        YOU PAID FOR SIDEWISP DURING THE THREE MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM. THESE LIMITATIONS DO
        NOT APPLY WHERE PROHIBITED BY LAW.
      </p>
    ),
  },
  {
    id: 'indemnity',
    title: '14. Indemnification',
    content: (
      <p>
        To the extent permitted by law, you will defend, indemnify, and hold harmless GOLEMWORKERS LLC and its
        personnel from claims, losses, and expenses arising from your misuse of Sidewisp, your data, your connected
        systems, or your violation of these Terms or applicable law.
      </p>
    ),
  },
  {
    id: 'law',
    title: '15. Governing Law',
    content: (
      <p>
        These Terms are governed by the laws of the State of Wyoming, United States, without regard to conflict-of-law
        rules. Courts located in Wyoming will have exclusive jurisdiction, except where applicable consumer law
        requires otherwise.
      </p>
    ),
  },
  {
    id: 'changes',
    title: '16. Changes to These Terms',
    content: (
      <p>
        We may update these Terms. We will post the revised version and update the effective date. If a change is
        material, we will provide additional notice when reasonably practicable. Continued use after the effective
        date means you accept the updated Terms.
      </p>
    ),
  },
]

const privacySections: LegalSection[] = [
  {
    id: 'scope',
    title: '1. Scope and Controller',
    content: (
      <p>
        This Privacy Policy explains how GOLEMWORKERS LLC collects, uses, shares, and protects personal information
        when you visit sidewisp.com, join the waitlist, use Sidewisp, or communicate with us. GOLEMWORKERS LLC is the
        controller of that information unless a separate agreement says otherwise.
      </p>
    ),
  },
  {
    id: 'collect',
    title: '2. Information We Collect',
    content: (
      <>
        <h3>Information you provide</h3>
        <ul>
          <li>email address and waitlist preferences, including the agent runtimes you select;</li>
          <li>Google or Apple provider, provider account identifier, verified email or private relay email when
            supplied by the provider, and linked-account status;</li>
          <li>account, workspace, profile, normalized store-subscription status, and support information;</li>
          <li>encrypted App Store or Google Play purchase references and store-event audit records, but not
            payment-card details;</li>
          <li>communications, feedback, and information you choose to send us.</li>
        </ul>
        <h3>Information from Sidewisp and connected systems</h3>
        <ul>
          <li>installation identifiers, runtime and collector versions, configuration and connection status;</li>
          <li>agent-health signals, run status, errors, timestamps, usage, cost, and incident metadata;</li>
          <li>actions requested, approved, rejected, or verified, together with related audit records.</li>
        </ul>
        <h3>Information collected automatically</h3>
        <ul>
          <li>IP address, browser, device, operating system, referral URL, pages viewed, and approximate location;</li>
          <li>UTM parameters and advertising click identifiers such as gclid, wbraid, and gbraid;</li>
          <li>security, diagnostic, and server logs.</li>
        </ul>
        <p>
          Sidewisp is designed to minimize collection. The public waitlist does not ask for agent credentials,
          private prompts, message content, or payment-card numbers.
        </p>
      </>
    ),
  },
  {
    id: 'sources',
    title: '3. Sources of Information',
    content: (
      <p>
        We receive information from you, your browser or device, Sidewisp collectors and integrations you authorize,
        service providers, and advertising or analytics partners. Organization administrators may also provide
        information about workspace members.
      </p>
    ),
  },
  {
    id: 'use',
    title: '4. How We Use Information',
    content: (
      <ul>
        <li>provide the website, waitlist, accounts, monitoring, alerts, diagnostics, and support;</li>
        <li>authenticate users, maintain integrations, and keep audit and security records;</li>
        <li>measure reliability, understand product usage, and improve Sidewisp;</li>
        <li>communicate about access, product changes, service messages, and requested marketing;</li>
        <li>verify App Store and Google Play transactions and administer subscription access;</li>
        <li>detect fraud, abuse, security threats, and violations of our Terms; and</li>
        <li>comply with law and protect our rights, users, and the public.</li>
      </ul>
    ),
  },
  {
    id: 'bases',
    title: '5. Legal Bases',
    content: (
      <p>
        Where data-protection law requires a legal basis, we rely on performance of a contract, steps requested
        before entering a contract, our legitimate interests in operating and securing Sidewisp, consent where
        required, and compliance with legal obligations.
      </p>
    ),
  },
  {
    id: 'sharing',
    title: '6. How We Share Information',
    content: (
      <>
        <p>We may share information with:</p>
        <ul>
          <li>infrastructure, hosting, database, email, security, and support providers;</li>
          <li>Google Tag Manager and Google Ads for site measurement and advertising attribution;</li>
          <li>Yandex Metrica for site analytics, with Webvisor disabled;</li>
          <li>Google and Apple for consumer sign-in, and Apple App Store or Google Play for store subscriptions;</li>
          <li>integrations you direct Sidewisp to connect with;</li>
          <li>professional advisers, authorities, or other parties when required by law or necessary to protect rights;</li>
          <li>a buyer or successor in connection with a merger, financing, reorganization, or sale.</li>
        </ul>
        <p>We do not sell personal information for money.</p>
      </>
    ),
  },
  {
    id: 'analytics',
    title: '7. Analytics and Advertising Measurement',
    content: (
      <p>
        We use measurement technologies to understand site use and attribute waitlist registrations to campaigns.
        Google advertising personalization signals are disabled in our site configuration. Yandex Webvisor and
        automatic goals are disabled. Providers may process device, network, page-view, and click-attribution data
        under their own policies. You can use browser controls, content blockers, and provider opt-out tools to limit
        this processing.
      </p>
    ),
  },
  {
    id: 'transfers',
    title: '8. International Transfers',
    content: (
      <p>
        We are based in the United States and use providers that may process information in the United States and
        other countries. Where required, we use recognized transfer mechanisms and contractual protections. Local
        privacy laws in those countries may differ from those where you live.
      </p>
    ),
  },
  {
    id: 'retention',
    title: '9. Retention',
    content: (
      <p>
        We retain information for as long as needed to provide Sidewisp, maintain security and audit records, resolve
        disputes, comply with law, and enforce agreements. Retention depends on the data type, account status, and
        legal or operational need. When information is no longer needed, we delete or de-identify it. Limited copies
        may remain in backups until they rotate out.
      </p>
    ),
  },
  {
    id: 'choices',
    title: '10. Your Rights and Choices',
    content: (
      <>
        <p>
          Depending on where you live, you may have rights to access, correct, delete, restrict, object to, or receive
          a copy of personal information, and to withdraw consent. You may also have the right to appeal a decision
          or complain to a data-protection authority.
        </p>
        <p>
          You may unsubscribe from non-essential email through the link in the message or by contacting us. To make a
          privacy request, email <a href={`mailto:${company.email}`}>{company.email}</a>. We may need to verify your
          identity before completing a request.
        </p>
        <p>
          Deleting your Sidewisp account revokes Sidewisp sessions and product access, but does not cancel an App
          Store or Google Play subscription or remove records the store must retain. Cancel or request a refund in
          the originating store. Sidewisp then retains only the minimum store and security records required for
          reconciliation, fraud prevention, legal compliance, and backup rotation.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    title: '11. Security',
    content: (
      <p>
        We use administrative, technical, and organizational safeguards designed to protect information, including
        access controls and encryption in transit. No method of transmission or storage is completely secure, so we
        cannot guarantee absolute security.
      </p>
    ),
  },
  {
    id: 'children',
    title: '12. Children',
    content: (
      <p>
        Sidewisp is not directed to children under 18, and we do not knowingly collect personal information from
        them. Contact us if you believe a child has provided personal information.
      </p>
    ),
  },
  {
    id: 'changes',
    title: '13. Changes to This Policy',
    content: (
      <p>
        We may update this Privacy Policy. We will post the revised version and update the effective date. If changes
        are material, we will provide additional notice when reasonably practicable.
      </p>
    ),
  },
]

const documents: Record<LegalDocumentKind, {
  eyebrow: string
  title: string
  intro: ReactNode
  sections: LegalSection[]
}> = {
  terms: {
    eyebrow: 'LEGAL / TERMS',
    title: 'Terms of Service',
    intro: (
      <>
        These Terms govern your access to and use of Sidewisp, a product operated by {company.name}. By accessing or
        using Sidewisp, you agree to these Terms. If you do not agree, do not use the Service.
      </>
    ),
    sections: termsSections,
  },
  privacy: {
    eyebrow: 'LEGAL / PRIVACY',
    title: 'Privacy Policy',
    intro: (
      <>
        This policy describes the personal information we process for the Sidewisp website, waitlist, private preview,
        and product services.
      </>
    ),
    sections: privacySections,
  },
}

function CompanyLockup() {
  return (
    <span className="legal-company-lockup">
      <img src="/brand/golem-workers-mark.svg" width="24" height="26" alt="Golem Workers" />
      <strong>GOLEM WORKERS</strong>
    </span>
  )
}

export default function LegalPage({ kind }: { kind: LegalDocumentKind }) {
  const document = documents[kind]

  useEffect(() => {
    syncLegalDocumentSeo(kind)
  }, [kind])

  return (
    <div className="legal-shell">
      <header className="legal-header">
        <a className="legal-brand" href="/" aria-label="Sidewisp home">
          <span className="legal-brand__mark" aria-hidden="true" />
          <strong>SIDEWISP</strong>
        </a>
        <a className="legal-header__company" href="https://golemworkers.com/" target="_blank" rel="noopener noreferrer">
          <span>OPERATED BY</span>
          <CompanyLockup />
        </a>
      </header>

      <main className="legal-main">
        <aside className="legal-rail" aria-label="Document navigation">
          <span>ON THIS PAGE</span>
          <nav>
            {document.sections.map((section) => <a href={`#${section.id}`} key={section.id}>{section.title}</a>)}
          </nav>
        </aside>

        <article className="legal-document">
          <header className="legal-document__hero">
            <span>{document.eyebrow}</span>
            <h1>{document.title}</h1>
            <p>{document.intro}</p>
            <dl>
              <div><dt>Effective</dt><dd>July 23, 2026</dd></div>
              <div><dt>Operator</dt><dd>{company.name}</dd></div>
              <div><dt>Product</dt><dd>Sidewisp</dd></div>
            </dl>
          </header>

          <div className="legal-document__body">
            {document.sections.map((section) => (
              <section id={section.id} key={section.id}>
                <h2>{section.title}</h2>
                {section.content}
              </section>
            ))}

            <section id="contact" className="legal-contact">
              <span>CONTACT</span>
              <h2>{kind === 'terms' ? '17. Contact' : '14. Contact Us'}</h2>
              <p>{company.name}<br />{company.address}</p>
              <a href={`mailto:${company.email}`}>{company.email}</a>
            </section>
          </div>
        </article>
      </main>

      <footer className="legal-footer">
        <span>© 2026 SIDEWISP</span>
        <nav aria-label="Legal links">
          <a href="/terms/" aria-current={kind === 'terms' ? 'page' : undefined}>Terms</a>
          <a href="/privacy/" aria-current={kind === 'privacy' ? 'page' : undefined}>Privacy</a>
          <a href="/">Back to Sidewisp</a>
        </nav>
        <a href="https://golemworkers.com/" target="_blank" rel="noopener noreferrer"><CompanyLockup /></a>
      </footer>
    </div>
  )
}
